/**
 * Communication Board Action Bar Editor
 * 
 * Allows caregivers to configure which action items (Speak, Send, Delete Word, Clear)
 * are visible in the Communication Board top action bar, with drag-to-reorder support
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { Check, Undo2, RotateCcw, GripVertical, Delete, Volume2, Mail, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import {
  CommunicationActionBarConfig,
  CommunicationActionItem,
  loadCommunicationActionBarConfig,
  saveCommunicationActionBarConfig,
  updateCommunicationActionItem,
  reorderCommunicationActionItems,
  resetCommunicationActionBarConfig,
} from "@/lib/communicationActionBarTypes";
import { useUnsavedEditGuard } from "@/hooks/useUnsavedEditGuard";

const ACTION_BAR_STORAGE_KEY = "communicationActionBarConfig_v1";

const MAX_UNDO_HISTORY = 20;

// Icon map for action items
const ACTION_ICON_MAP: Record<string, React.ElementType> = {
  Delete,
  Trash2,
  Volume2,
  Mail,
};

export default function CommunicationActionBarEditor() {
  const navigate = useNavigate();
  const { t, dir, isRTL } = useCaregiverTranslation();
  
  const [config, setConfig] = useState<CommunicationActionBarConfig>(() => loadCommunicationActionBarConfig());
  const [undoHistory, setUndoHistory] = useState<CommunicationActionBarConfig[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const isUndoing = useRef(false);

  const { markSaved } = useUnsavedEditGuard({
    storageKeys: [ACTION_BAR_STORAGE_KEY],
    onAfterRevert: () => setConfig(loadCommunicationActionBarConfig()),
  });

  // Save to localStorage when config changes
  useEffect(() => {
    if (!isUndoing.current) {
      saveCommunicationActionBarConfig(config);
    }
  }, [config]);

  // Clear drag state on window events
  useEffect(() => {
    const clearDragState = () => setDraggedIndex(null);
    window.addEventListener("dragend", clearDragState);
    window.addEventListener("drop", clearDragState);
    window.addEventListener("mouseup", clearDragState);
    window.addEventListener("touchend", clearDragState);
    return () => {
      window.removeEventListener("dragend", clearDragState);
      window.removeEventListener("drop", clearDragState);
      window.removeEventListener("mouseup", clearDragState);
      window.removeEventListener("touchend", clearDragState);
    };
  }, []);

  // Save to undo history before making changes
  const saveToHistory = useCallback(() => {
    setUndoHistory(prev => {
      const newHistory = [...prev, config];
      if (newHistory.length > MAX_UNDO_HISTORY) {
        return newHistory.slice(-MAX_UNDO_HISTORY);
      }
      return newHistory;
    });
  }, [config]);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoHistory.length === 0) return;
    
    isUndoing.current = true;
    const previousState = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));
    setConfig(previousState);
    saveCommunicationActionBarConfig(previousState);
    setDraggedIndex(null);
    
    setTimeout(() => {
      isUndoing.current = false;
    }, 0);
  }, [undoHistory]);

  // Toggle action item enabled state
  const handleToggleAction = (itemId: string, enabled: boolean) => {
    setDraggedIndex(null);
    saveToHistory();
    const updatedConfig = updateCommunicationActionItem(config, itemId, { enabled });
    setConfig(updatedConfig);
  };

  // Handle reset to defaults
  const handleResetToDefault = () => {
    saveToHistory();
    const defaultConfig = resetCommunicationActionBarConfig();
    setConfig(defaultConfig);
    setDraggedIndex(null);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    saveToHistory();
    const newConfig = reorderCommunicationActionItems(config, draggedIndex, index);
    setConfig(newConfig);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Get sorted items
  const sortedActionItems = [...config.actionItems].sort((a, b) => a.order - b.order);

  return (
    <MainLayout onBack={() => navigate(-1)}>
      <div className="flex-1 flex flex-col overflow-hidden" dir={dir}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-card/50 border-b border-border">
          <h1 className="text-xl font-bold text-foreground">
            {t("editTopActionBar")}
          </h1>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleUndo}
              disabled={undoHistory.length === 0}
            >
              <Undo2 className="w-4 h-4 mr-2" />
              {t("undo")}
            </Button>
            
            <Button variant="outline" size="sm" onClick={handleResetToDefault}>
              <RotateCcw className="w-4 h-4 mr-2" />
              {t("resetToDefault")}
            </Button>
            
            <Button size="sm" disabled={undoHistory.length === 0} onClick={() => { markSaved(); navigate('/board-editor'); }}>
              <Check className="w-4 h-4 mr-2" />
              {t("doneEditing")}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="max-w-2xl mx-auto space-y-8">
            {/* Description */}
            <p className="text-muted-foreground text-sm">
              {isRTL 
                ? "התאם את הכפתורים בשורת הפעולות העליונה של לוח התקשורת. ניתן להפעיל/לכבות כפתורים ולשנות את הסדר שלהם ע\"י גרירה."
                : "Customize the buttons in the Communication Board's top action bar. You can enable/disable buttons and reorder them by dragging."
              }
            </p>

            {/* Action Items Section */}
            <section>
              <h2 className="text-lg font-semibold mb-4 text-foreground">
                {isRTL ? "כפתורי פעולה" : "Action Buttons"}
              </h2>
              <div className="space-y-2">
                {sortedActionItems.map((item, index) => {
                  const IconComponent = ACTION_ICON_MAP[item.iconName] || Delete;
                  const label = isRTL ? item.labelHe : item.labelEn;
                  
                  return (
                    <motion.div
                      key={item.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={cn(
                        "flex items-center gap-4 p-4 rounded-xl border-2 bg-card",
                        "transition-all duration-200 cursor-move",
                        draggedIndex === index && "opacity-50 scale-95",
                        item.enabled ? "border-primary/50" : "border-border opacity-60"
                      )}
                      whileHover={{ scale: 1.01 }}
                    >
                      <GripVertical className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center",
                        item.enabled ? "bg-primary/20" : "bg-muted"
                      )}>
                        <IconComponent className={cn(
                          "w-6 h-6",
                          item.enabled ? "text-primary" : "text-muted-foreground"
                        )} />
                      </div>
                      
                      <span className={cn(
                        "flex-1 font-medium",
                        item.enabled ? "text-foreground" : "text-muted-foreground"
                      )}>
                        {label}
                      </span>
                      
                      <Switch
                        checked={item.enabled}
                        onCheckedChange={(checked) => handleToggleAction(item.id, checked)}
                      />
                    </motion.div>
                  );
                })}
              </div>
            </section>

          </div>
        </div>
      </div>
    </MainLayout>
  );
}
